import { useState, useEffect, useImperativeHandle, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { NoData, INoDataProps } from "@components/new/NoData";
import Pagination from '@components/Pagination';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { SearchInfo } from './components/SearchInfo';
import { usePagingSearchParams, PaginationModel } from '@hooks/usePagingSearchParams';
import styles from "./DataView.module.scss";

export interface BeforeProps<T, Params=void> {
  items: T[];
  isLoading: boolean;
  total: number;
  filters?: FilterModel[];
  params?: Params;
}

export type TListenerTableData<T> = (data: T[], total: number) => void;

export type DataViewHandle<T> = {
  refresh: () => void,
  total: number,
  data?: T[],
}

export type DataViewProps<T, VP, BP=void, AP=void> = {
  pending?: boolean;
  queryKey?: string | string[];
  getData: (data: {
    page: PaginationModel,
    filters?: FilterModel[],
    sorting?: { column: string, direction: string };
  }) => Promise<({ items: T[], total: number })>;
  notFound?: React.FC;
  notFoundDescription?: string;
  filters?: FilterModel[];
  sorting?: { column: string, direction: string },
  Before?: React.FC<BeforeProps<T, BP>>;
  After?: React.FC<BeforeProps<T, AP>>;
  View: React.FC<any>; // ViewExtendedProps<T, ViewProps>
  viewProps?: VP;
  beforeProps?: BP;
  afterProps?: AP;
  defaultPageSize?: number;
  dataRef?: React.RefObject<DataViewHandle<T>>;
  withPagination?: boolean;
  onUpdate?: TListenerTableData<T>;
  noLoaderOnRefresh?: boolean;
  noDataProps?: INoDataProps;
  skeleton?: ReactNode;
  hidePaginationDropdown?: boolean;
  resetOnUpdate?: boolean;
  pageSizeOptions?: number[];
  keepSearchParams?: boolean;
  keepSelectionOnFiltersChange?: boolean;
}

const MAX_PAGE_SIZE = 99999;

function DataView<T, ViewProps, BP = void, AP = void>({
  pending,
  getData,
  queryKey,
  notFound,
  // notFoundDescription,
  filters,
  sorting,
  Before,
  beforeProps,
  After,
  afterProps,
  View,
  viewProps,
  defaultPageSize,
  dataRef,
  withPagination,
  onUpdate,
  noLoaderOnRefresh,
  noDataProps,
  skeleton,
  hidePaginationDropdown,
  resetOnUpdate,
  pageSizeOptions,
  keepSearchParams,
}: DataViewProps<T, ViewProps, BP, AP>) {
  const [currentPage, setCurrentPage] = usePagingSearchParams(1, defaultPageSize || 10, keepSearchParams);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [viewKey, setViewKey] = useState(1);

  const { pathname } = useLocation();

  const showPagination = withPagination ?? true;

  const query = useQuery({
    queryKey: [
      ...(Array.isArray(queryKey) ? queryKey : [queryKey]),
      currentPage.page,
      currentPage.pageSize,
      JSON.stringify(filters),
      JSON.stringify(sorting),
    ],
    queryFn: () => getData({
      page: currentPage,
      filters,
      sorting,
    }),
    onSuccess: () => {
      (resetOnUpdate ?? true) && setViewKey(viewKey + 1) // todo - попробуем удалить этот костыль позже
    },
    retry: 1,
    enabled: !pending,
    staleTime: 0,
    // cacheTime: 0,
  });
  const { items, total: totalItems } = query.data || { items: [], total: 0 };
  const isLoading = pending || (noLoaderOnRefresh ? query.isLoading : query.isFetching);

  useImperativeHandle(dataRef, () => ({
    refresh: () => {
      query.refetch();
    },
    total: query.data?.total ?? 0,
    data: query.data?.items,
  }));

  const updatePagination = (page: number, pageSize: number) => {
    setCurrentPage({ page, pageSize });
  }

  useEffect(() => {
    if (!pending) {
      setIsInitialized(true);
    }
  }, [pending]);

  useEffect(() => {
    if (isInitialized) {
      const condition: boolean = pathname === '/pick-up';
      const trackNumberFilter: FilterModel | undefined = (filters || []).find((filter: FilterModel) => filter.name === 'trackOrExternalNumbers' || filter.name === 'trackNumbers');

      setCurrentPage({
        page: 1,
        pageSize: condition && trackNumberFilter && trackNumberFilter.value.length && trackNumberFilter.value[0].split(',').length > 1
            ? MAX_PAGE_SIZE
            : currentPage.pageSize === MAX_PAGE_SIZE
                ? defaultPageSize ?? 10
                : currentPage.pageSize
      });
    }
  }, [isInitialized, pathname, JSON.stringify(filters)]);

  useEffect(() => {
    if (
      query.data?.total
      && !query.data?.items?.length
      && currentPage.page > 1
    ) {
      setCurrentPage({ page: 1, pageSize: currentPage.pageSize });
    }
    if (window.location.search.includes('tab=awaiting')) {
      localStorage.setItem('awaitingParcels', JSON.stringify(items.map((item: any) => item.id)));
    }
    if (window.location.search.includes('tab=disposal')) {
      localStorage.setItem('disposalParcels', JSON.stringify(items.map((item: any) => item.id)));
    }
    if (window.location.search.includes('tab=resale')) {
      localStorage.setItem('resaleParcels', JSON.stringify(items.map((item: any) => item.id)));
    }
    if (window.location.search.includes('tab=returns')) {
      localStorage.setItem('returnsParcels', JSON.stringify(items.map((item: any) => item.id)));
    }
    onUpdate && onUpdate(query.data?.items ?? [], query.data?.total ?? 0);
  }, [query.data?.total, query.data?.items?.length]);

  if (isLoading) {
    return (
      <div className={styles.root}>
        {Before && (
          <Before
            items={items ?? []}
            filters={filters}
            total={query.data?.total || 0}
            isLoading={isLoading}
            params={beforeProps}
          />
        )}
        {skeleton}
        {After && (
          <After
            items={items ?? []}
            filters={filters}
            total={query.data?.total || 0}
            isLoading={isLoading}
            params={afterProps}
          />
        )}
      </div>
    )
  }

  const NotFound = notFound;

  if (items && !isLoading && !items.length) {
    return (
      <div className={styles.root}>
        {Before && (
          <Before
            items={items ?? []}
            filters={filters}
            total={query.data?.total || 0}
            isLoading={isLoading}
            params={beforeProps}
          />
        )}
        {NotFound ? (<NotFound />) : (
          <NoData
            title={noDataProps?.title ?? ""}
            description={noDataProps?.description ?? ""}
            btnTitle={noDataProps?.btnTitle}
            onClick={noDataProps?.onClick}
            width={noDataProps?.width}
          />
        )}
        {After && (
          <After
            items={items ?? []}
            filters={filters}
            total={query.data?.total || 0}
            isLoading={isLoading}
            params={afterProps}
          />
        )}
      </div>
    )
  }

  return (
    <div className={styles.root}>
      {Before && (
        <Before
          items={items ?? []}
          filters={filters}
          total={query.data?.total || 0}
          isLoading={isLoading}
          params={beforeProps}
        />
      )}
      <View
        key={`data-view-${viewKey}`}
        {...viewProps}
        data={items ?? [] as T[]}
        filters={filters}
      />
      <SearchInfo
        items={items ?? []}
        filters={filters}
        total={query.data?.total || 0}
        isLoading={isLoading}
      />
      {After && (
        <After
          items={items ?? []}
          filters={filters}
          total={query.data?.total || 0}
          isLoading={isLoading}
          params={afterProps}
        />
      )}
      {(!showPagination || (currentPage.pageSize === MAX_PAGE_SIZE)) ? null : (
        <div className={styles.paginationRow}>
          <Pagination
            currentPage={currentPage.page}
            totalItems={totalItems}
            pageSize={currentPage.pageSize}
            onChange={updatePagination}
            onSizeChange={updatePagination}
            hidePaginationDropdown={hidePaginationDropdown}
            pageSizeOptions={pageSizeOptions}
            isNew
          />
        </div>
      )}
    </div>
  );
}

DataView.displayName = "DataView";
export default DataView;
